import { createBetOutbound, createCallOutbound, createCheckOutbound, createFoldOutbound, createRaiseOutbound } from '../../../api/outbound/pokerOutbounds';
import { isConfigFlagEnabled } from '../../../config';
import { getShortRenderedValueStr } from '../../../utils/numberPrecisionUtils';
import { getSetBetActionsFromMyTurnProperties } from '../../../utils/setBetActionUtils';
import { assertUnreachable } from '../../../utils/tsUtils';
export const SIMPLE_BET_ALL_IN_LABEL = 'ALL-IN';
export const ALL_SIMPLE_BET_ACTION_PROPERTIES = {
    fold: {
        labelBase: 'FOLD',
        testId: 'fold-button',
        labelPixiDataType: 'fold-button-label',
        subLabelPixiDataType: 'fold-button-sub-label'
    },
    check: {
        labelBase: 'CHECK',
        testId: 'check-button',
        labelPixiDataType: 'check-button-label',
        subLabelPixiDataType: 'check-button-sub-label'
    },
    call: {
        labelBase: 'CALL',
        testId: 'call-button',
        labelPixiDataType: 'call-button-label',
        subLabelPixiDataType: 'call-button-sub-label'
    },
    '2x': {
        labelBase: '2X',
        testId: 'bet-2x-button',
        labelPixiDataType: 'bet-2x-button-label',
        subLabelPixiDataType: 'bet-2x-button-sub-label'
    },
    '3x': {
        labelBase: '3X',
        testId: 'bet-3x-button',
        labelPixiDataType: 'bet-3x-button-label',
        subLabelPixiDataType: 'bet-3x-button-sub-label'
    },
    '5x': {
        labelBase: '5X',
        testId: 'bet-5x-button',
        labelPixiDataType: 'bet-5x-button-label',
        subLabelPixiDataType: 'bet-5x-button-sub-label'
    },
    'quarter-pot': {
        labelBase: '1/4',
        testId: 'bet-quarter-pot-button',
        labelPixiDataType: 'bet-quarter-pot-button-label',
        subLabelPixiDataType: 'bet-quarter-pot-button-sub-label'
    },
    'half-pot': {
        labelBase: '1/2',
        testId: 'bet-half-pot-button',
        labelPixiDataType: 'bet-half-pot-button-label',
        subLabelPixiDataType: 'bet-half-pot-button-sub-label'
    },
    'three-quarter-pot': {
        labelBase: '3/4',
        testId: 'bet-three-quarter-pot-button',
        labelPixiDataType: 'bet-three-quarter-pot-button-label',
        subLabelPixiDataType: 'bet-three-quarter-pot-button-sub-label'
    },
    pot: {
        labelBase: 'POT',
        testId: 'bet-pot-button',
        labelPixiDataType: 'bet-pot-button-label',
        subLabelPixiDataType: 'bet-pot-button-sub-label'
    },
    max: {
        labelBase: 'MAX',
        testId: 'bet-max-button',
        labelPixiDataType: 'bet-max-button-label',
        subLabelPixiDataType: 'bet-max-button-sub-label'
    }
};
// array of strings to use when generating bitmap fonts for simple betting button labels:
export const ALL_SIMPLE_BET_BUTTON_LABELS = Object.values(ALL_SIMPLE_BET_ACTION_PROPERTIES)
    .map(({ labelBase }) => labelBase)
    .concat(' ')
    .concat('BET')
    .concat('RAISE')
    .concat(SIMPLE_BET_ALL_IN_LABEL);
function createCallButtonProperties(myTurnProperties, socketSend) {
    const additionalChipsToCall = myTurnProperties.betTotalAmountToCall - myTurnProperties.myTotalBetAmount;
    const isAllInToCall = myTurnProperties.myNumChips <= additionalChipsToCall;
    const simpleBetActionProperties = ALL_SIMPLE_BET_ACTION_PROPERTIES.call;
    const label = isAllInToCall ? SIMPLE_BET_ALL_IN_LABEL : simpleBetActionProperties.labelBase;
    const subLabel = isAllInToCall
        ? getShortRenderedValueStr(myTurnProperties.myNumChips)
        : getShortRenderedValueStr(additionalChipsToCall);
    return {
        simpleBetActionType: 'call',
        label,
        subLabel,
        onClick: () => {
            socketSend(createCallOutbound(myTurnProperties.betSequenceNumber));
        },
        accentColor: 'blue'
    };
}
function createBetOrRaiseOnClick(myTurnProperties, betSliderAmount, socketSend) {
    if (!myTurnProperties.betOrRaiseOutboundType) {
        // can't bet or raise
        return;
    }
    switch (myTurnProperties.betOrRaiseOutboundType) {
        case 'bet': {
            return () => {
                socketSend(createBetOutbound(myTurnProperties.betSequenceNumber, betSliderAmount));
            };
        }
        case 'raise': {
            return () => {
                const raiseAmountForOutbound = betSliderAmount - (myTurnProperties.betTotalAmountToCall - myTurnProperties.myTotalBetAmount);
                socketSend(createRaiseOutbound(myTurnProperties.betSequenceNumber, raiseAmountForOutbound));
            };
        }
        default: {
            assertUnreachable(myTurnProperties.betOrRaiseOutboundType);
        }
    }
}
function getBetOrRaiseLabel(labelType, labelBase, betSliderAmount, myNumChips) {
    if (betSliderAmount >= myNumChips) {
        return SIMPLE_BET_ALL_IN_LABEL;
    }
    switch (labelType) {
        case 'bet': {
            return `BET ${labelBase}`;
        }
        case 'raise': {
            return `RAISE ${labelBase}`;
        }
    }
}
export function createAllSimpleBetButtonProperties(myTurnProperties, totalPotSize, socketSend) {
    const allButtonProperties = [];
    if (myTurnProperties.canCheck) {
        const { labelBase } = ALL_SIMPLE_BET_ACTION_PROPERTIES.check;
        allButtonProperties.push({
            simpleBetActionType: 'check',
            label: labelBase,
            onClick: () => {
                socketSend(createCheckOutbound(myTurnProperties.betSequenceNumber));
            },
            accentColor: 'blue'
        });
    }
    if (myTurnProperties.canCall) {
        allButtonProperties.push(createCallButtonProperties(myTurnProperties, socketSend));
    }
    if (myTurnProperties.canFold) {
        const { labelBase } = ALL_SIMPLE_BET_ACTION_PROPERTIES.fold;
        allButtonProperties.push({
            simpleBetActionType: 'fold',
            label: labelBase,
            onClick: () => {
                socketSend(createFoldOutbound(myTurnProperties.betSequenceNumber));
            },
            accentColor: 'red'
        });
    }
    // add bet/raise buttons:
    const setBetActions = getSetBetActionsFromMyTurnProperties(myTurnProperties, totalPotSize);
    setBetActions.forEach((setBetAction) => {
        if (!setBetAction) {
            return;
        }
        if (!myTurnProperties.betOrRaiseLabelType) {
            // can't bet or raise
            return;
        }
        const onClick = createBetOrRaiseOnClick(myTurnProperties, setBetAction.betSliderAmount, socketSend);
        if (!onClick) {
            return;
        }
        const { labelBase } = ALL_SIMPLE_BET_ACTION_PROPERTIES[setBetAction.setBetActionType];
        const label = getBetOrRaiseLabel(myTurnProperties.betOrRaiseLabelType, labelBase, setBetAction.betSliderAmount, myTurnProperties.myNumChips);
        allButtonProperties.push({
            simpleBetActionType: setBetAction.setBetActionType,
            label,
            subLabel: getShortRenderedValueStr(setBetAction.betSliderAmount + myTurnProperties.myNumChipsThisPhase),
            onClick,
            accentColor: 'green'
        });
    });
    if (isConfigFlagEnabled('simpleBettingDataDebugLog')) {
        console.log('simple betting data: myTurnProperties', JSON.stringify(myTurnProperties));
        console.log('simple betting data: totalPotSize', totalPotSize);
        console.log('simple betting data: allButtonProperties', JSON.stringify(allButtonProperties));
    }
    return allButtonProperties;
}
const PASSIVE_BET_ACTION_TYPES = ['check', 'call', 'fold'];
export function splitSimpleBetButtonsIntoGroups(allButtonProperties) {
    const passiveButtonProperties = [];
    const aggressiveButtonProperties = [];
    allButtonProperties.forEach((buttonProperties) => {
        if (PASSIVE_BET_ACTION_TYPES.includes(buttonProperties.simpleBetActionType)) {
            passiveButtonProperties.push(buttonProperties);
        }
        else {
            aggressiveButtonProperties.push(buttonProperties);
        }
    });
    return { passiveButtonProperties, aggressiveButtonProperties };
}
